import React, { useState, useEffect } from 'react';
import { Card, Button, Input } from '../ui';
import { useSalon } from '../../contexts/SalonContext';
import { 
  Cog6ToothIcon, 
  ScissorsIcon, 
  UsersIcon, 
  ChevronRightIcon,
  BellIcon,
  UserCircleIcon,
  LockClosedIcon,
  ArrowLeftIcon,
  MagnifyingGlassIcon,
  ChatBubbleLeftRightIcon,
  ServerIcon,
  PaintBrushIcon,
  QrCodeIcon,
  LinkIcon,
  BellAlertIcon
} from '@heroicons/react/24/outline';

// Importando os componentes que serão navegados
import ServiceManagement from './ServiceManagement';
import ProfessionalManagement from './ProfessionalManagement';
import WhatsAppTemplatesManager from './WhatsAppTemplatesManager';
import NotificationSettings from './settings/NotificationSettings';
import CacheMonitor from '../CacheMonitor';
import SalonVisualSettings from './settings/SalonVisualSettings';
import QRCodeSettings from './settings/QRCodeSettings';
import { useUserPreferences } from '../../hooks/useUserPreferences';
import SalonSettings from './settings/SalonSettings';
import NoticesSettings from './settings/NoticesSettings';

/**
 * Componente para gerenciar as configurações do salão
 * Inclui acesso a Serviços e Profissionais
 */
const SettingsManagement = ({ salonId, salonEmail, isAdmin, onLogout }) => {
  const { salonConfig } = useSalon();
  const { preferences, updatePreference } = useUserPreferences();
  const [isMobile, setIsMobile] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Estado para controlar qual tela está sendo exibida
  const [activeScreen, setActiveScreen] = useState('main');
  
  // Verificar se é dispositivo móvel baseado no tamanho da tela
  useEffect(() => {
    const checkIfMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkIfMobile();
    window.addEventListener('resize', checkIfMobile);
    
    return () => {
      window.removeEventListener('resize', checkIfMobile);
    };
  }, []);
  
  // Opções do menu de configurações
  const settingsOptions = [
    {
      id: 'services',
      name: 'Serviços',
      description: 'Gerenciar serviços oferecidos pelo salão',
      icon: ScissorsIcon,
      component: ServiceManagement,
      category: 'salon'
    },
    {
      id: 'professionals',
      name: 'Profissionais',
      description: 'Gerenciar profissionais do salão',
      icon: UsersIcon,
      component: ProfessionalManagement,
      category: 'salon'
    },
    {
      id: 'notifications',
      name: 'Notificações',
      description: 'Gerenciar preferências de notificações',
      icon: BellIcon,
      component: NotificationSettings,
      category: 'communication'
    },
    {
      id: 'whatsapptemplates',
      name: 'Templates de WhatsApp',
      description: 'Gerenciar modelos de mensagens para clientes',
      icon: ChatBubbleLeftRightIcon,
      component: WhatsAppTemplatesManager,
      category: 'communication'
    },
    {
      id: 'visual',
      name: 'Personalização Visual',
      description: 'Personalizar cores, fontes e logo do salão',
      icon: PaintBrushIcon,
      component: SalonVisualSettings,
      category: 'salon'
    },
    {
      id: 'qrcode',
      name: 'Link da Agenda',
      description: 'Gerar e gerenciar link de agendamento do salão',
      icon: LinkIcon,
      component: QRCodeSettings,
      category: 'salon'
    },
    {
      id: 'notices',
      name: 'Avisos Personalizados',
      description: 'Configurar avisos para clientes',
      icon: BellAlertIcon,
      component: NoticesSettings,
      category: 'salon'
    },
    {
      id: 'salon',
      name: 'Configurações do Salão',
      description: 'Gerenciar informações gerais do salão',
      icon: Cog6ToothIcon,
      component: SalonSettings,
      category: 'salon'
    },
    {
      id: 'cachemonitor',
      name: 'Monitor de Cache',
      description: 'Visualizar e gerenciar o cache da aplicação',
      icon: ServerIcon,
      component: () => <CacheMonitor salonId={salonId} salonEmail={salonEmail} isAdmin={isAdmin} />,
      category: 'cache'
    }
  ];
  
  // Configurações adicionais para o mobile (estilo Instagram)
  const mobileSettingsOptions = [
    // Categoria Sua Conta
    {
      id: 'account',
      name: 'Sua conta',
      icon: UserCircleIcon,
      description: 'Gerenciar informações da sua conta',
      rightElement: null,
      category: 'account',
      component: null
    },
    
    // Opção Link da Agenda na categoria Sua conta
    {
      id: 'qrcode',
      name: 'Link da Agenda',
      icon: LinkIcon,
      description: 'Gerar e gerenciar link de agendamento do salão',
      rightElement: null,
      category: 'account',
      component: QRCodeSettings
    },
    
    // Botão de Sair
    {
      id: 'logout',
      name: 'Sair',
      icon: LockClosedIcon,
      description: 'Sair da sua conta',
      rightElement: null,
      category: 'account',
      action: () => {
        if (onLogout) {
          onLogout();
        }
        // Redirecionar para a página de login
        window.location.href = '/login';
      }
    },
    
    // Categoria Como você usa o app
    {
      id: 'notifications',
      name: 'Notificações',
      icon: BellIcon,
      description: 'Gerenciar notificações',
      rightElement: null,
      category: 'usage',
      component: NotificationSettings
    },
    
    // Categoria Comunicação
    {
      id: 'whatsapptemplates',
      name: 'Templates de WhatsApp',
      icon: ChatBubbleLeftRightIcon,
      description: 'Gerenciar modelos de mensagens para clientes',
      rightElement: null,
      category: 'communication',
      component: WhatsAppTemplatesManager
    },
    
    // Categoria Cache
    {
      id: 'cachemonitor',
      name: 'Monitor de Cache',
      icon: ServerIcon,
      description: 'Visualizar e gerenciar o cache da aplicação',
      rightElement: null,
      category: 'cache',
      component: () => <CacheMonitor salonId={salonId} salonEmail={salonEmail} isAdmin={isAdmin} />
    },
    
    // Categoria Configurações do Salão
    ...settingsOptions.filter(option => 
      option.id !== 'whatsapptemplates' && 
      option.id !== 'notifications' &&
      option.id !== 'cachemonitor' &&
      option.id !== 'qrcode'
    )
  ];
  
  // Filtrar opções com base no termo de pesquisa (para mobile)
  const filteredOptions = searchTerm 
    ? mobileSettingsOptions.filter(option => 
        option.name.toLowerCase().includes(searchTerm.toLowerCase()) || 
        (option.description && option.description.toLowerCase().includes(searchTerm.toLowerCase()))
      )
    : mobileSettingsOptions;
  
  // Agrupar opções por categoria para o layout mobile
  const groupedOptions = filteredOptions.reduce((acc, option) => {
    if (!acc[option.category]) {
      acc[option.category] = [];
    }
    acc[option.category].push(option);
    return acc;
  }, {});
  
  // Função para renderizar o cabeçalho para dispositivos móveis (estilo Instagram)
  const renderMobileHeader = () => (
    <div className="sticky top-0 bg-white z-10 border-b border-gray-200">
      <div className="flex items-center p-4">
        <button 
          onClick={() => setActiveScreen('main')}
          className="mr-4"
        >
          <ArrowLeftIcon className="h-6 w-6 text-black" />
        </button>
        <h1 className="text-xl font-semibold">Configurações e atividade</h1>
      </div>
    </div>
  );

  // Função para renderizar a tela principal em layout mobile (estilo Instagram)
  const renderMobileMainScreen = () => {
    // Definições de categoria para exibição
    const categoryTitles = {
      'account': 'Sua conta',
      'salon': 'Configurações do Salão',
      'usage': 'Como você usa o app',
      'communication': 'Comunicação',
      'cache': 'Cache',
      'visibility': 'Privacidade'
    };

    // Ordem definida das categorias para exibição
    const categoryOrder = ['account', 'salon', 'usage', 'communication', 'cache', 'visibility'];

    return (
      <div className="bg-gray-50 min-h-screen pb-16">
        {renderMobileHeader()}
        
        <div className="mt-2">
          {categoryOrder.map(category => (
            groupedOptions[category] && groupedOptions[category].length > 0 && (
              <div key={category} className="mb-4">
                {/* Título da categoria */}
                <h2 className="px-4 py-2 text-sm font-semibold text-gray-500">
                  {categoryTitles[category] || category}
                </h2>
                
                {/* Itens da categoria */}
                <div className="bg-white">
                  {groupedOptions[category].map((option, index) => (
                    <button
                      key={option.id}
                      onClick={() => option.action ? option.action() : setActiveScreen(option.id)}
                      className={`w-full px-4 py-3 flex items-center justify-between text-left border-gray-100 ${
                        index !== groupedOptions[category].length - 1 ? 'border-b' : ''
                      }`}
                    >
                      <div className="flex items-center">
                        <div className="w-8 h-8 flex-shrink-0">
                          <option.icon className="h-6 w-6 text-gray-800" />
                        </div>
                        <span className="ml-3 text-base">{option.name}</span>
                      </div>
                      <div className="flex items-center">
                        {option.rightElement && (
                          <span className="mr-2 text-gray-500 text-sm">{option.rightElement}</span>
                        )}
                        {!option.action && <ChevronRightIcon className="h-5 w-5 text-gray-400" />}
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )
          ))}
        </div>
      </div>
    );
  };
  
  // Função para renderizar a tela principal em layout desktop (existente)
  const renderDesktopMainScreen = () => (
    <div className="space-y-4">
      <h2 className="text-2xl font-bold text-gray-800 mb-4">Configurações</h2>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {settingsOptions.map((option) => (
          <Card key={option.id} className="p-0 overflow-hidden">
            <button
              onClick={() => setActiveScreen(option.id)}
              className="w-full p-4 text-left flex items-center hover:bg-gray-50 transition-colors"
            >
              <div className="flex-shrink-0 h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center mr-4">
                <option.icon className="h-6 w-6 text-blue-600" />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-medium text-gray-800">{option.name}</h3>
                <p className="text-sm text-gray-500">{option.description}</p>
              </div>
              <ChevronRightIcon className="h-5 w-5 text-gray-400" />
            </button>
          </Card>
        ))}
      </div>
    </div>
  );
  
  // Função para renderizar o componente da tela selecionada
  const renderScreen = () => {
    if (activeScreen === 'main') {
      return isMobile ? renderMobileMainScreen() : renderDesktopMainScreen();
    }
    
    // Para mobile, encontrar nas opções móveis também
    const allOptions = [...settingsOptions, ...mobileSettingsOptions.filter(opt => 
      !settingsOptions.some(s => s.id === opt.id))];
    const selectedOption = allOptions.find(option => option.id === activeScreen);
    
    if (!selectedOption || !selectedOption.component) {
      return (
        <div className={`text-center p-8 ${isMobile ? 'min-h-screen bg-white' : ''}`}>
          {isMobile && (
            <div className="sticky top-0 bg-white z-10 border-b border-gray-200 mb-4">
              <div className="flex items-center p-4">
                <button 
                  onClick={() => setActiveScreen('main')}
                  className="mr-4"
                >
                  <ArrowLeftIcon className="h-6 w-6 text-black" />
                </button>
                <h1 className="text-xl font-semibold">{selectedOption?.name || 'Voltar'}</h1>
              </div>
            </div>
          )}
          
          <h3 className="text-lg font-medium text-gray-800 mb-2">Recurso em Desenvolvimento</h3>
          <p className="text-gray-500 mb-4">Esta funcionalidade será implementada em breve.</p>
          {!isMobile && (
            <Button onClick={() => setActiveScreen('main')} variant="outline">
              Voltar para Configurações
            </Button>
          )}
        </div>
      );
    }
    
    const ScreenComponent = selectedOption.component;
    return (
      <div className={isMobile ? 'min-h-screen bg-white' : ''}>
        <div className={`${isMobile ? 'sticky top-0 bg-white z-10 border-b border-gray-200' : 'mb-4'}`}>
          {isMobile ? (
            <div className="flex items-center p-4">
              <button 
                onClick={() => setActiveScreen('main')}
                className="mr-4"
              >
                <ArrowLeftIcon className="h-6 w-6 text-black" />
              </button>
              <h1 className="text-xl font-semibold">{selectedOption.name}</h1>
            </div>
          ) : (
            <>
              <Button onClick={() => setActiveScreen('main')} variant="outline" className="mb-4">
                ← Voltar para Configurações
              </Button>
              {/* <h2 className="text-2xl font-bold text-gray-800">{selectedOption.name}</h2> */}
            </>
          )}
        </div>
        
        <ScreenComponent />
      </div>
    );
  };

  return (
    <div className="w-full">
      {renderScreen()}
    </div>
  );
};

export default SettingsManagement; 